import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, NG_VALIDATORS, ValidationErrors, Validator, AbstractControl } from '@angular/forms';
import { TimeRangeValidators } from '../../validators';

export interface DayTimeSlot {
    from: string;
    to: string;
    cutOff: number;
    slotInterval: string;
}

@Component({
    selector: 'app-day-time-slot',
    templateUrl: './day-time-slot.component.html',
    styleUrls: ['./day-time-slot.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DayTimeSlotComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DayTimeSlotComponent),
            multi: true
        }
    ]
})
export class DayTimeSlotComponent implements ControlValueAccessor, Validator {
    @Input() disabled: boolean = false;

    dayForm: FormGroup;

    private onChange = (value: DayTimeSlot) => { };
    private onTouched = () => { };
    private onValidatorChange = () => { };

    constructor(
        private fb: FormBuilder,
    ) {
        this.dayForm = this.fb.group({
            from: [''],
            to: [''],
            cutOff: [''],
            slotInterval: ['']
        }, { validators: TimeRangeValidators.timeRange("from", "to") });

        this.dayForm.valueChanges.subscribe(value => {
            this.onChange(value);
            this.onTouched();
        });
        this.dayForm.statusChanges.subscribe(() => {
            this.onValidatorChange();
        });
    }

    // ControlValueAccessor implementation
    writeValue(value: DayTimeSlot): void {
        if (value) {
            this.dayForm.patchValue(value, { emitEvent: false });
        }
    }

    registerOnChange(fn: (value: DayTimeSlot) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.dayForm.disable();
        } else {
            this.dayForm.enable();
        }
    }

    // Validator implementation
    validate(control: AbstractControl): ValidationErrors | null {
        if (this.dayForm && this.dayForm.invalid) {
            return { dayTimeSlotInvalid: true };
        }
        return null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    // Time validation methods
    validateTimeRange(): void {
        // Trigger form validation using the TimeRangeValidators
        this.dayForm.updateValueAndValidity();
    }

    onTimeChange(): void {
        this.validateTimeRange();
        // Trigger validation update for parent form
        this.onValidatorChange();
    }

    onValueChange(): void {
        // Trigger validation update for parent form when any value changes
        this.onValidatorChange();
    }

    get fromControl() {
        return this.dayForm.get('from');
    }

    get toControl() {
        return this.dayForm.get('to');
    }

    get cutOffControl() {
        return this.dayForm.get('cutOff');
    }

    get slotIntervalControl() {
        return this.dayForm.get('slotInterval');
    }
}
